import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;

const ROLES = [
  { value: "super_admin", label: "مدير رئيسي", color: "#f59e0b", bg: "#fef3c7" },
  { value: "admin", label: "مدير", color: "#3b82f6", bg: "#dbeafe" },
  { value: "content_manager", label: "مدير محتوى", color: "#10b981", bg: "#d1fae5" },
  { value: "media_manager", label: "مدير وسائط", color: "#8b5cf6", bg: "#ede9fe" },
  { value: "reviewer", label: "مراجع", color: "#6366f1", bg: "#e0e7ff" },
  { value: "employee", label: "موظف", color: "#6b7280", bg: "#f3f4f6" },
];

const ROLE_MAP = Object.fromEntries(ROLES.map(r => [r.value, r]));

export default function StaffStatsPanel({ token }) {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRole, setEditingRole] = useState(null); // {staff_id, name}
  const [staffList, setStaffList] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadStats(); loadStaff(); }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/staff/stats`, { headers });
      setStats(data || []);
    } catch { toast.error("خطأ في تحميل إحصاءات الموظفين"); }
    setLoading(false);
  };

  const loadStaff = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/staff`, { headers });
      setStaffList(data || []);
    } catch {}
  };

  const updateRole = async (staff_id, role) => {
    try {
      await axios.put(`${API}/admin/staff/${staff_id}/role`, { studio_role: role }, { headers });
      toast.success("تم تحديث الدور");
      setEditingRole(null);
      loadStaff();
    } catch { toast.error("فشل تحديث الدور"); }
  };

  const getRoleBadge = (role) => {
    const r = ROLE_MAP[role] || ROLE_MAP.employee;
    return (
      <span className="text-xs px-2 py-0.5 rounded-full font-bold"
        style={{ color: r.color, background: r.bg }}>
        {r.label}
      </span>
    );
  };

  const medals = ["🥇", "🥈", "🥉"];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-black">👥 إحصاءات الموظفين والأدوار</h2>
          <p className="text-primary/50 text-sm mt-1">أداء الموظفين وإدارة الصلاحيات</p>
        </div>
        <button onClick={() => { loadStats(); loadStaff(); }}
          className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-bold hover:bg-primary/20 transition-all">
          ↻ تحديث
        </button>
      </div>

      {/* Role assignment section */}
      <div className="bg-white border border-primary/10 rounded-2xl p-5 mb-6">
        <div className="font-black text-primary mb-4">🔐 إدارة الأدوار (RBAC)</div>
        {staffList.length === 0 ? (
          <div className="text-primary/40 text-sm">لا يوجد موظفون حتى الآن</div>
        ) : (
          <div className="space-y-2">
            {staffList.map(s => (
              <div key={s.id} className="flex items-center gap-3 p-3 rounded-xl bg-primary/5">
                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center font-black text-blue-700">
                  {(s.display_name || s.username)[0]?.toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="font-bold text-sm text-primary">{s.display_name || s.username}</div>
                  <div className="text-xs text-primary/40">@{s.username}</div>
                </div>
                <div className="flex items-center gap-2">
                  {getRoleBadge(s.studio_role || "employee")}
                  {editingRole?.staff_id === s.id ? (
                    <div className="flex items-center gap-1">
                      <select
                        defaultValue={s.studio_role || "employee"}
                        onChange={e => updateRole(s.id, e.target.value)}
                        className="border-2 border-primary/20 rounded-lg px-2 py-1 text-xs font-bold outline-none">
                        {ROLES.filter(r => r.value !== "super_admin").map(r => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                      <button onClick={() => setEditingRole(null)}
                        className="text-xs text-primary/40 hover:text-primary px-2">✕</button>
                    </div>
                  ) : (
                    <button onClick={() => setEditingRole({ staff_id: s.id })}
                      className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-lg font-bold hover:bg-primary/20 transition-all">
                      تعديل الدور
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stats leaderboard */}
      <div className="font-black text-primary mb-4">📊 مساهمات الموظفين (من سجل النشاط)</div>

      {loading ? (
        <div className="text-center py-16 text-primary/40">⏳ جارٍ التحميل...</div>
      ) : stats.length === 0 ? (
        <div className="text-center py-12 text-primary/30">
          <div className="text-4xl mb-2">📋</div>
          <div>لا توجد بيانات نشاط بعد</div>
        </div>
      ) : (
        <div className="space-y-3">
          {stats.map((s, i) => (
            <div key={s.name}
              className={`bg-white border border-primary/10 rounded-xl p-4 ${i < 3 ? "ring-2" : ""}`}
              style={i === 0 ? { ringColor: "#f59e0b" } : i === 1 ? { ringColor: "#94a3b8" } : i === 2 ? { ringColor: "#cd7f32" } : {}}>
              <div className="flex items-center gap-3 mb-3">
                <div className="text-2xl w-8 text-center">
                  {i < 3 ? medals[i] : <span className="text-sm text-primary/40 font-black">#{i + 1}</span>}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-black text-primary">{s.name}</span>
                    {getRoleBadge(s.studio_role)}
                  </div>
                  {s.last_action && (
                    <div className="text-xs text-primary/40 mt-0.5">
                      آخر نشاط: {new Date(s.last_action).toLocaleDateString("ar-SA")}
                    </div>
                  )}
                </div>
                <div className="text-2xl font-black text-primary">{s.total}</div>
              </div>

              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {[
                  { label: "أسئلة", value: s.questions, color: "#10b981" },
                  { label: "تعديلات", value: s.edits, color: "#3b82f6" },
                  { label: "حذف", value: s.deletes, color: "#ef4444" },
                  { label: "صور", value: s.images, color: "#f59e0b" },
                  { label: "صوت", value: s.audio, color: "#8b5cf6" },
                  { label: "فئات", value: s.categories, color: "#6366f1" },
                ].map((stat, j) => (
                  <div key={j} className="text-center bg-primary/5 rounded-lg p-2">
                    <div className="font-black text-sm" style={{ color: stat.color }}>{stat.value || 0}</div>
                    <div className="text-xs text-primary/40">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="flex gap-4 mt-2 text-xs text-primary/40">
                <span>اليوم: <strong className="text-primary">{s.today || 0}</strong></span>
                <span>الأسبوع: <strong className="text-primary">{s.week || 0}</strong></span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
